/**
 * Tiny safe inline-markdown renderer (Iter 145+)
 *
 * Supports the minimum admins actually need for tier descriptions:
 *   • **bold**
 *   • *italic*
 *   • [link text](https://example.com)
 *   • single \n  →  <br />
 *   • blank line →  paragraph break
 *
 * Importantly: NO dangerouslySetInnerHTML — every node is a React element,
 * so admin input cannot inject scripts or arbitrary HTML.
 *
 * Usage:
 *   import { renderInlineMarkdown } from '../../lib/inlineMarkdown';
 *   <span>{renderInlineMarkdown(tier.description)}</span>
 */

// Tokenise a single line into a list of React-friendly nodes.
// Order matters: links first (so `*italic*` inside link text is honoured),
// then bold (greedy double-star), then italic (single star).
const INLINE_RX = [
  // [text](url)
  { rx: /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, render: (m) => ({ kind: 'link', text: m[1], href: m[2] }) },
  // **bold**
  { rx: /\*\*([^*]+)\*\*/g, render: (m) => ({ kind: 'bold', text: m[1] }) },
  // *italic*
  { rx: /\*([^*]+)\*/g, render: (m) => ({ kind: 'italic', text: m[1] }) },
];

function tokeniseLine(line) {
  if (!line) return [];
  // Greedy walk — find the earliest match across all rules at each step.
  const tokens = [];
  let cursor = 0;
  while (cursor < line.length) {
    let nextStart = -1;
    let nextEnd = -1;
    let nextNode = null;
    for (const { rx, render } of INLINE_RX) {
      rx.lastIndex = cursor;
      const m = rx.exec(line);
      if (m && (nextStart === -1 || m.index < nextStart)) {
        nextStart = m.index;
        nextEnd = m.index + m[0].length;
        nextNode = render(m);
      }
    }
    if (nextStart === -1) {
      tokens.push({ kind: 'text', text: line.slice(cursor) });
      break;
    }
    if (nextStart > cursor) tokens.push({ kind: 'text', text: line.slice(cursor, nextStart) });
    tokens.push(nextNode);
    cursor = nextEnd;
  }
  return tokens;
}

export function renderInlineMarkdown(input) {
  if (!input || typeof input !== 'string') return null;
  // Split on blank lines for paragraph breaks; within a paragraph,
  // single \n becomes a <br />.
  const paragraphs = input.replace(/\r\n/g, '\n').split(/\n\s*\n/);
  return paragraphs.map((para, pi) => {
    const lines = para.split('\n');
    return (
      <span key={pi} style={{ display: 'block', marginBottom: pi < paragraphs.length - 1 ? '0.7em' : 0 }}>
        {lines.map((line, li) => {
          const tokens = tokeniseLine(line);
          return (
            <span key={li}>
              {tokens.map((t, ti) => {
                if (t.kind === 'bold') return <strong key={ti}>{t.text}</strong>;
                if (t.kind === 'italic') return <em key={ti}>{t.text}</em>;
                if (t.kind === 'link') {
                  return (
                    <a
                      key={ti}
                      href={t.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--vl-neon, #A78BFA)', textDecoration: 'underline' }}
                    >
                      {t.text}
                    </a>
                  );
                }
                return <span key={ti}>{t.text}</span>;
              })}
              {li < lines.length - 1 && <br />}
            </span>
          );
        })}
      </span>
    );
  });
}

/**
 * Split a benefit string into `{ icon, text }`.
 *
 * Supported forms (in priority order):
 *   "🎁 10% deposit bonus"          → { icon: '🎁', text: '10% deposit bonus' }
 *   "[gift] 10% deposit bonus"      → { icon: '[gift]', text: '10% deposit bonus' }
 *   "10% deposit bonus"             → { icon: null,  text: '10% deposit bonus' }
 *
 * The icon segment is the first whitespace-delimited token if it's a single
 * emoji (or a `[shortcode]` square-bracket marker). Otherwise the whole
 * string is treated as text and the consumer renders a fallback bullet.
 */
const EMOJI_RX = /^(\p{Extended_Pictographic}|\p{Emoji_Presentation})(\u200D(\p{Extended_Pictographic}|\p{Emoji_Presentation}))*\uFE0F?/u;

export function parseBenefit(raw) {
  if (!raw || typeof raw !== 'string') return { icon: null, text: '' };
  const trimmed = raw.trim();
  // Square-bracket shortcode like [gift]
  const shortcodeMatch = trimmed.match(/^\[([a-zA-Z0-9_-]+)\]\s+(.+)$/);
  if (shortcodeMatch) {
    return { icon: `[${shortcodeMatch[1]}]`, text: shortcodeMatch[2].trim() };
  }
  // Leading emoji
  const emojiMatch = trimmed.match(EMOJI_RX);
  if (emojiMatch) {
    const icon = emojiMatch[0];
    const rest = trimmed.slice(icon.length).trim();
    if (rest) return { icon, text: rest };
  }
  return { icon: null, text: trimmed };
}
